/**
 * Automations endpoints — port of v1 ``ando-frontend/app/api/automations.ts``
 * (240 LOC) + ``ando-frontend/app/types/automations.ts`` (332 LOC).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/automations.py``
 *   - ``ando-agent/src/api/v1/endpoints/notifications.py``
 *   - ``ando-agent/src/schemas/automations.py``
 *
 * Every wire shape mirrors the BE Pydantic model exactly — keep the field
 * names + nullability in lockstep. When the BE evolves, this file is the
 * first thing to grow.
 *
 * Conventions:
 *   - Endpoint paths are written without the ``/api/v1`` prefix (the shared
 *     ``apiClient`` from ``../ApiClient.ts`` already prepends it via the
 *     base URL).
 *   - Helpers return the raw envelope shape; TanStack Query hooks live in
 *     ``apps/client/src/features/automations/hooks/`` and are responsible
 *     for caching + polling.
 */

import { apiClient } from "../ApiClient.js";

// ── Common literals ─────────────────────────────────────────────────────

export type ScheduleType = "cron" | "interval" | "once" | "webhook" | "reactive";
export type OutputChannelType = "email" | "in_app_notification" | "webhook";

// ── Nested config shapes (mirror Pydantic models 1:1) ───────────────────

export interface AgentConfig {
  /** May 26, 2026 — two-tier mode selector. Replaces ``search_power`` +
   * ``thinking_mode``. */
  mode?: "fast" | "deep";
  web_search_mode?: boolean;
  focus?: "standard" | "structured" | "full";
  max_cost_per_run?: number | null;
}

export interface ApprovalConfig {
  approvers: string[];
  auto_approve_below_confidence?: number;
  timeout_hours?: number;
  timeout_action?: "cancel" | "send";
}

export interface IntentFilter {
  enabled: boolean;
  description: string;
}

export interface ReactiveConfig {
  sources?: string[];
  connector_ids?: string[];
  filters?: Record<string, unknown>;
  event_types?: string[];
  email_accounts?: string[];
  sender_allowlist?: string[];
  sender_blocklist?: string[];
  channel_ids?: string[];
  max_triggers_per_hour?: number | null;
  cooldown_seconds?: number | null;
  intent_filter?: IntentFilter | null;
}

// ── Reactive Sources ────────────────────────────────────────────────────

export interface ReactiveSource {
  connection_id: string;
  connection_type: "email" | "chat";
  provider: string;
  event_type: string;
  display_name?: string | null;
  email_address?: string | null;
}

// ── Create / Update payloads ────────────────────────────────────────────

export interface AutomationCreatePayload {
  name: string;
  description?: string | null;
  prompt: string;
  schedule_type: ScheduleType;
  schedule_preset?: string | null;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  run_at?: string | null;
  timezone?: string;
  source_config?: ReactiveConfig;
  output_channels?: Array<Record<string, unknown>>;
  agent_config?: AgentConfig;
  requires_approval?: boolean;
  approval_config?: ApprovalConfig | null;
  max_runs?: number | null;
}

export interface AutomationUpdatePayload {
  name?: string;
  description?: string | null;
  prompt?: string;
  schedule_type?: ScheduleType;
  schedule_preset?: string | null;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  run_at?: string | null;
  timezone?: string;
  source_config?: ReactiveConfig;
  output_channels?: Array<Record<string, unknown>>;
  agent_config?: AgentConfig;
  requires_approval?: boolean;
  approval_config?: ApprovalConfig | null;
  max_runs?: number | null;
}

// ── Responses ───────────────────────────────────────────────────────────

export interface Automation {
  automation_id: string;
  user_id: string;
  name: string;
  description?: string | null;
  prompt: string;
  schedule_type: ScheduleType;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  run_at?: string | null;
  timezone: string;
  source_config: ReactiveConfig | Record<string, unknown>;
  output_config: Record<string, unknown>;
  agent_config: AgentConfig & Record<string, unknown>;
  requires_approval: boolean;
  approval_config?: Record<string, unknown> | null;
  is_active: boolean;
  status: string;
  next_run_at?: string | null;
  last_run_at?: string | null;
  total_runs: number;
  consecutive_failures: number;
  max_runs?: number | null;
  template_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutomationListResponse {
  items: Automation[];
  total: number;
  page: number;
  page_size: number;
}

export interface AutomationRun {
  run_id: string;
  automation_id: string;
  user_id: string;
  status: string;
  trigger_type: string;
  trigger_data?: Record<string, unknown>;
  result_content?: string | null;
  result_metadata?: Record<string, unknown>;
  delivery_status?: Array<Record<string, unknown>>;
  error_message?: string | null;
  error_type?: string | null;
  duration_ms?: number | null;
  total_cost_usd?: number | null;
  generated_files?: Array<Record<string, unknown>>;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

export interface AutomationRunListResponse {
  items: AutomationRun[];
  total: number;
  page: number;
  page_size: number;
}

export interface AutomationStats {
  total_automations: number;
  active: number;
  paused: number;
  error: number;
  runs_this_month: number;
  success_rate: number;
  total_cost_this_month_usd: number;
}

export interface AutomationTemplate {
  template_id: string;
  name: string;
  description: string;
  category: string;
  prompt: string;
  schedule_type: ScheduleType;
  default_config?: Record<string, unknown>;
  required_channels?: OutputChannelType[];
  icon?: string;
}

export interface UserNotification {
  notification_id: string;
  user_id: string;
  title: string;
  body: string;
  notification_type: string;
  priority: string;
  automation_id?: string | null;
  run_id?: string | null;
  action_url?: string | null;
  action_type?: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: UserNotification[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditEntry {
  audit_id: string;
  user_id: string;
  automation_id: string;
  run_id?: string | null;
  event_type: string;
  event_details: Record<string, unknown>;
  created_at: string;
}

export interface AuditListResponse {
  items: AuditEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface DryRunResponse {
  preview: string;
  sources?: string[] | null;
  estimated_cost?: Record<string, unknown> | null;
}

export interface WebhookUrlResponse {
  webhook_url: string;
  webhook_secret: string;
  curl_example: string;
}

export interface ParseScheduleResponse {
  schedule_type: string;
  cron_expression: string | null;
  interval_minutes: number | null;
  description: string;
}

export interface DailyRunStats {
  date: string;
  total: number;
  success: number;
  failed: number;
}

export interface DailyCostStats {
  date: string;
  cost_usd: number;
}

export interface AnalyticsResponse {
  daily_runs: DailyRunStats[];
  daily_cost: DailyCostStats[];
  by_trigger_type: Record<string, number>;
  top_automations: Array<{
    name: string;
    runs: number;
    success_rate: number;
    avg_duration_ms: number | null;
  }>;
  avg_duration_ms: number | null;
}

// ── CRUD ───────────────────────────────────────────────────────────────

/** GET ``/automations`` — paginated list. */
export async function listAutomations(args: {
  page?: number;
  pageSize?: number;
  status?: string;
} = {}): Promise<AutomationListResponse> {
  const params = new URLSearchParams({
    page: String(args.page ?? 1),
    page_size: String(args.pageSize ?? 20),
  });
  if (args.status) params.set("status", args.status);
  return apiClient.get<AutomationListResponse>(`/automations?${params.toString()}`);
}

/** GET ``/automations/{id}`` — detail. */
export async function getAutomation(automationId: string): Promise<Automation> {
  return apiClient.get<Automation>(`/automations/${automationId}`);
}

/** POST ``/automations`` — create. */
export async function createAutomation(
  payload: AutomationCreatePayload,
): Promise<Automation> {
  return apiClient.post<Automation>("/automations", payload);
}

/** PUT ``/automations/{id}`` — update. Note: BE uses PUT, not PATCH. */
export async function updateAutomation(
  automationId: string,
  payload: AutomationUpdatePayload,
): Promise<Automation> {
  return apiClient.put<Automation>(`/automations/${automationId}`, payload);
}

/** DELETE ``/automations/{id}`` — archive (soft delete). */
export async function deleteAutomation(automationId: string): Promise<void> {
  await apiClient.delete(`/automations/${automationId}`);
}

// ── Lifecycle ──────────────────────────────────────────────────────────

export interface RunStarted {
  run_id: string;
  status: string;
}

export async function runAutomation(automationId: string): Promise<RunStarted> {
  return apiClient.post<RunStarted>(
    `/automations/${automationId}/run`,
    {},
  );
}

export async function pauseAutomation(
  automationId: string,
): Promise<{ automation_id: string; status: string }> {
  return apiClient.post<{ automation_id: string; status: string }>(
    `/automations/${automationId}/pause`,
    {},
  );
}

export async function resumeAutomation(
  automationId: string,
): Promise<{ automation_id: string; status: string }> {
  return apiClient.post<{ automation_id: string; status: string }>(
    `/automations/${automationId}/resume`,
    {},
  );
}

export async function duplicateAutomation(automationId: string): Promise<Automation> {
  return apiClient.post<Automation>(
    `/automations/${automationId}/duplicate`,
    {},
  );
}

export async function dryRunAutomation(automationId: string): Promise<DryRunResponse> {
  return apiClient.post<DryRunResponse>(
    `/automations/${automationId}/dry-run`,
    {},
  );
}

// ── Runs / approvals / retry ───────────────────────────────────────────

export async function listAutomationRuns(args: {
  automationId: string;
  page?: number;
  pageSize?: number;
}): Promise<AutomationRunListResponse> {
  const params = new URLSearchParams({
    page: String(args.page ?? 1),
    page_size: String(args.pageSize ?? 20),
  });
  return apiClient.get<AutomationRunListResponse>(
    `/automations/${args.automationId}/runs?${params.toString()}`,
  );
}

export async function approveRun(args: {
  automationId: string;
  runId: string;
  notes?: string;
}): Promise<{ status: string }> {
  return apiClient.post<{ status: string }>(
    `/automations/${args.automationId}/runs/${args.runId}/approve`,
    args.notes ? { notes: args.notes } : {},
  );
}

export async function rejectRun(args: {
  automationId: string;
  runId: string;
  notes?: string;
}): Promise<{ status: string }> {
  return apiClient.post<{ status: string }>(
    `/automations/${args.automationId}/runs/${args.runId}/reject`,
    args.notes ? { notes: args.notes } : {},
  );
}

export async function retryRun(args: {
  automationId: string;
  runId: string;
}): Promise<RunStarted> {
  return apiClient.post<RunStarted>(
    `/automations/${args.automationId}/runs/${args.runId}/retry`,
    {},
  );
}

// ── Reactive Sources / Templates ───────────────────────────────────────

export async function listReactiveSources(): Promise<{ sources: ReactiveSource[] }> {
  return apiClient.get<{ sources: ReactiveSource[] }>("/automations/reactive-sources");
}

export async function listTemplates(): Promise<AutomationTemplate[]> {
  const res = await apiClient.get<{ templates: AutomationTemplate[] }>(
    "/automations/templates",
  );
  return res.templates ?? [];
}

export async function createFromTemplate(templateId: string): Promise<Automation> {
  return apiClient.post<Automation>(
    `/automations/from-template/${templateId}`,
    {},
  );
}

// ── Webhook / Schedule helpers ─────────────────────────────────────────

export async function getWebhookUrl(automationId: string): Promise<WebhookUrlResponse> {
  return apiClient.get<WebhookUrlResponse>(
    `/automations/${automationId}/webhook-url`,
  );
}

export async function parseSchedule(
  text: string,
  timezone: string,
): Promise<ParseScheduleResponse> {
  return apiClient.post<ParseScheduleResponse>(
    "/automations/parse-schedule",
    { text, timezone },
  );
}

// ── Stats / Analytics ──────────────────────────────────────────────────

export async function getAutomationStats(): Promise<AutomationStats> {
  return apiClient.get<AutomationStats>("/automations/stats");
}

export async function getAutomationAnalytics(days = 30): Promise<AnalyticsResponse> {
  return apiClient.get<AnalyticsResponse>(`/automations/analytics?days=${days}`);
}

// ── Audit ──────────────────────────────────────────────────────────────

export async function getAuditLog(args: {
  automationId: string;
  page?: number;
  pageSize?: number;
}): Promise<AuditListResponse> {
  const params = new URLSearchParams({
    page: String(args.page ?? 1),
    page_size: String(args.pageSize ?? 50),
  });
  return apiClient.get<AuditListResponse>(
    `/automations/${args.automationId}/audit?${params.toString()}`,
  );
}

export async function restoreVersion(args: {
  automationId: string;
  auditId: string;
}): Promise<{ status: string }> {
  return apiClient.post<{ status: string }>(
    `/automations/${args.automationId}/restore-version/${args.auditId}`,
    {},
  );
}

// ── Notifications (separate router, kept here for adjacency) ───────────

export async function listNotifications(args: {
  page?: number;
  pageSize?: number;
} = {}): Promise<NotificationListResponse> {
  const params = new URLSearchParams({
    page: String(args.page ?? 1),
    page_size: String(args.pageSize ?? 20),
  });
  return apiClient.get<NotificationListResponse>(
    `/notifications?${params.toString()}`,
  );
}

export async function getUnreadCount(): Promise<{ unread_count: number }> {
  return apiClient.get<{ unread_count: number }>("/notifications/unread-count");
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await apiClient.post(`/notifications/${notificationId}/read`, {});
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post("/notifications/read-all", {});
}

export async function deleteNotification(notificationId: string): Promise<void> {
  await apiClient.delete(`/notifications/${notificationId}`);
}
